import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import { submissionService } from '../../services/submissionService';
import { notificationService } from '../../services/notificationService';
import { conferenceService } from '../../services/conferenceService';
import BadgeDownloadButton from '../badge/BadgeDownloadButton';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate, formatDateTime } from '../../utils/formatters';

/**
 * The full picture of one submission.
 *
 * This markup used to live on the dashboard, where it rendered five stacked
 * rows per paper — pipeline stepper, file names, review tallies, progress bar,
 * camera-ready upload. That inverted the two screens: the dashboard, which
 * should answer "what needs me", buried you in detail, while /my-submissions,
 * whose entire job is detail, showed a thin grouped list. The dashboard now
 * links here instead, and this is where the depth lives.
 */
const SubmissionCard = ({ submission, onRefresh, focused = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const [uploadingId, setUploadingId] = useState(null);
  const [errorForId, setErrorForId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [successForId, setSuccessForId] = useState(null);
  const fileInputRef = useRef(null);
  const pendingSubId = useRef(null);
  const permissionInputRef = useRef(null);
  const submissions = [submission];

const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Which document the file picker was opened for: 'camera_ready' or
  // 'permission_form'. One hidden input serves both, so it has to remember.
  const pendingKind = useRef('camera_ready');

  const handleCameraReadyClick = (submissionId) => {
    pendingSubId.current = submissionId;
    pendingKind.current = 'camera_ready';
    setErrorForId(null);
    setErrorMsg('');
    setSuccessForId(null);
    fileInputRef?.current?.click();
  };

  const handlePermissionFormClick = (submissionId) => {
    pendingSubId.current = submissionId;
    pendingKind.current = 'permission_form';
    setErrorForId(null);
    setErrorMsg('');
    setSuccessForId(null);
    permissionInputRef?.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e?.target?.files?.[0];
    const subId = pendingSubId.current;
    if (!file || !subId) return;

    // Reset file input immediately so same file can be re-selected
    if (fileInputRef?.current) fileInputRef.current.value = '';

    // The two documents have different formats on purpose: the camera-ready is
    // an editable Word file the proceedings editor works from, the permission
    // form is a signed PDF.
    //
    // Checked by extension as well as MIME type — browsers report .docm
    // inconsistently (and sometimes as application/octet-stream), so trusting
    // file.type alone rejects valid uploads on some machines.
    const isPermission = pendingKind.current === 'permission_form';
    const name = (file?.name || '').toLowerCase();
    const ext = name.slice(name.lastIndexOf('.'));

    const WORD_EXT = ['.doc', '.docx', '.docm'];
    const WORD_MIME = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-word.document.macroEnabled.12',
    ];

    const ok = isPermission
      ? ext === '.pdf' || file?.type === 'application/pdf'
      : WORD_EXT.includes(ext) || WORD_MIME.includes(file?.type);

    if (!ok) {
      setErrorForId(subId);
      setErrorMsg(isPermission
        ? t('mySubmissions.validation.onlyPdf', 'The permission form must be a PDF.')
        : t('mySubmissions.validation.onlyWord', 'The camera-ready must be a Word document (.doc, .docx or .docm).'));
      return;
    }
    if (file?.size > 100 * 1024 * 1024) {
      setErrorForId(subId);
      setErrorMsg(t('mySubmissions.validation.fileSizeLimit'));
      return;
    }

    setUploadingId(subId);
    setErrorForId(null);
    setErrorMsg('');

    try {
      if (pendingKind.current === 'permission_form') {
        await submissionService?.uploadPermissionForm(subId, file, user?.id);
      } else {
        await submissionService?.uploadCameraReady(subId, file, user?.id);
      }

      // Upload succeeded — show success immediately
      setUploadingId(null);
      setSuccessForId(subId);

      // Auto-clear success after 10 seconds
      setTimeout(() => setSuccessForId(prev => (prev === subId ? null : prev)), 10000);

      // Refresh submissions data in background (light refresh — won't unmount this component)
      if (onRefresh) onRefresh();

      // Send emails in background — don't await, don't block UI
      const submission = submissions?.find(s => s?.id === subId);
      if (submission) {
        const confName = submission?.conference?.name || submission?.conference?.shortName;
        const confId = submission?.conferenceId || submission?.conference?.id;
        const authorName = user?.user_metadata?.first_name || user?.email;

        notificationService?.sendCameraReadyConfirmationEmail(
          user?.email, authorName, submission?.title, confName, confId
        ).catch(err => console.error('Author email failed:', err));

        conferenceService?.getConferenceOrganizers(submission?.conferenceId || submission?.conference?.id)
          .then(orgData => {
            const fullAuthorName = `${user?.user_metadata?.first_name || ''} ${user?.user_metadata?.last_name || ''}`.trim() || user?.email;
            (orgData?.chairs || []).forEach(chair => {
              notificationService?.sendCameraReadyChairNotification(
                chair?.email, `${chair?.firstName} ${chair?.lastName}`,
                submission?.title, fullAuthorName, confName, confId
              ).catch(err => console.error('Chair notification failed:', err));
            });
          }).catch(err => console.error('Failed to load organizers:', err));
      }
    } catch (err) {
      console.error('Camera-ready upload error:', err);
      setUploadingId(null);
      setErrorForId(subId);
      setErrorMsg(err?.message || t('mySubmissions.validation.uploadFailed'));
    }
  };

  // ── Derive status metadata for each submission ──
  const getStatusConfig = (status) => {
    const map = {
      draft:              { label: t('mySubmissions.statuses.draft'),             color: 'text-muted-foreground',  bg: 'bg-muted',  icon: 'FileEdit' },
      submitted:          { label: t('mySubmissions.statuses.submitted'),         color: 'text-primary',   bg: 'bg-primary-subtle',   icon: 'Send' },
      under_review:       { label: t('mySubmissions.statuses.underReview'),      color: 'text-warning',  bg: 'bg-warning-subtle',  icon: 'Eye' },
      accepted:           { label: t('mySubmissions.statuses.accepted'),          color: 'text-success', bg: 'bg-success-subtle', icon: 'CheckCircle2' },
      rejected:           { label: t('mySubmissions.statuses.rejected'),      color: 'text-error ',    bg: 'bg-error-subtle ',    icon: 'XCircle' },
      revision_required:  { label: t('mySubmissions.statuses.revisionRequired'), color: 'text-warning',  bg: 'bg-warning-subtle',  icon: 'AlertCircle' },
      withdrawn:          { label: t('mySubmissions.statuses.withdrawn'),         color: 'text-muted-foreground',  bg: 'bg-muted',  icon: 'MinusCircle' },
    };
    return map[status] || map.draft;
  };

  const getNextAction = (submission) => {
    const s = submission;
    const hasCR = !!s?.cameraReadySubmittedAt || successForId === s?.id;
    const crDeadline = s?.conference?.cameraReadyDeadline;
    const crPassed = crDeadline && new Date(crDeadline) < new Date();

    if (s?.status === 'draft') return { text: t('mySubmissions.nextActions.completeAndSubmit'), type: 'action', action: 'continue' };
    if (s?.status === 'submitted') return { text: t('mySubmissions.nextActions.waitingForReviewAssignment'), type: 'waiting' };
    if (s?.status === 'under_review') {
      const done = s?.reviewInfo?.completedReviews || 0;
      const total = s?.reviewInfo?.totalReviews || 0;
      if (total === 0) return { text: t('mySubmissions.nextActions.waitingForReviewerAssignment'), type: 'waiting' };
      if (done < total) return { text: t('mySubmissions.nextActions.reviewInProgress', { done, total }), type: 'waiting' };
      return { text: t('mySubmissions.nextActions.allReviewsComplete'), type: 'waiting' };
    }
    if (s?.status === 'accepted') {
      // Accepted *with conditions*. The paper is in, but the author owes
      // changes — say so before mentioning the camera-ready, because the
      // changes are what the camera-ready has to contain.
      if (s?.revisionSeverity) {
        return {
          text: t(
            s.revisionSeverity === 'major'
              ? 'mySubmissions.nextActions.majorRevision'
              : 'mySubmissions.nextActions.minorRevision',
            s.revisionSeverity === 'major'
              ? 'Accepted — major revisions required before your camera-ready.'
              : 'Accepted — minor revisions required before your camera-ready.'
          ),
          type: 'action',
          action: 'camera_ready',
        };
      }
      if (!crDeadline) return { text: t('mySubmissions.nextActions.noFurtherAction'), type: 'done' };
      if (hasCR) return { text: t('mySubmissions.nextActions.allDoneCameraReady'), type: 'done' };
      // Accepted authors can always upload a camera-ready, even past the deadline.
      if (crPassed) return { text: t('mySubmissions.nextActions.cameraReadyDeadlinePassedCanUpload', 'The camera-ready deadline has passed, but you can still upload.'), type: 'action', action: 'camera_ready' };
      return { text: t('mySubmissions.nextActions.uploadCameraReady', { deadline: formatDate(crDeadline) }), type: 'action', action: 'camera_ready' };
    }
    if (s?.status === 'revision_required') return { text: t('mySubmissions.nextActions.reviseAndResubmit'), type: 'action', action: 'continue' };
    if (s?.status === 'rejected') return { text: t('mySubmissions.nextActions.noFurtherAction'), type: 'done' };
    return { text: '', type: 'none' };
  };


  const pipelineSteps = [t('mySubmissions.pipeline.submitted'), t('mySubmissions.pipeline.inReview'), t('mySubmissions.pipeline.decision'), t('mySubmissions.pipeline.cameraReady')];
  const getStepIndex = (s) => {
    const st = s?.status;
    if (st === 'draft') return -1;
    if (st === 'submitted') return 0;
    if (st === 'under_review') return 1;
    if (['accepted', 'rejected', 'revision_required'].includes(st)) {
      if (st === 'accepted' && (s?.cameraReadySubmittedAt || successForId === s?.id)) return 3;
      return 2;
    }
    return -1;
  };

  if (!submission) return null;

  const statusCfg = getStatusConfig(submission?.status);
  const nextAction = getNextAction(submission);
  const isAccepted = submission?.status === 'accepted';
  const hasCR = !!submission?.cameraReadySubmittedAt;
  const justUploaded = successForId === submission?.id;
  const hasError = errorForId === submission?.id;
  const isUploading = uploadingId === submission?.id;
  const stepIdx = getStepIndex(submission);

  return (
    <div
      id={`submission-${submission?.id}`}
      className={`bg-card border overflow-hidden shadow-sm rounded-lg transition-colors ${
        focused ? 'border-primary ring-2 ring-primary/30' : 'border-border'
      }`}
    >
      <input ref={fileInputRef} type="file" accept=".doc,.docx,.docm" className="hidden" onChange={handleFileChange} />
      {/* Separate input so the accept filter differs: a signed form is a PDF. */}
      <input ref={permissionInputRef} type="file" accept=".pdf" className="hidden" onChange={handleFileChange} />
      <div className="px-5 py-4">
                {/* Row 1: Title + Status badge */}
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-foreground leading-snug">{submission?.title}</h3>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <Icon name="Building2" size={11} className="flex-shrink-0" />
                        {submission?.conference?.shortName || submission?.conference?.name}
                      </span>
                      {submission?.participantId && (
                        <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 font-medium text-primary">
                          <Icon name="Badge" size={11} className="flex-shrink-0" />
                          ID: {submission?.participantId}
                        </span>
                      )}
                      {submission?.trackName && (
                        <span>
                          <span className="text-border">|</span> {submission?.trackName}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${statusCfg.bg} ${statusCfg.color}`}>
                      <Icon name={statusCfg.icon} size={13} />
                      {statusCfg.label}
                    </div>
                  </div>
                </div>

                {/* Row 2: Mini pipeline stepper (only for non-draft) */}
                {submission?.status !== 'draft' && submission?.status !== 'withdrawn' && (
                  <div className="flex items-center gap-0 mb-3">
                    {pipelineSteps.map((step, i) => {
                      const isCompleted = i <= stepIdx;
                      const isCurrent = i === stepIdx;
                      const isRejected = submission?.status === 'rejected' && i === 2;
                      return (
                        <React.Fragment key={step}>
                          {i > 0 && (
                            <div className={`h-[2px] flex-1 ${isCompleted ? (isRejected ? 'bg-error-subtle' : 'bg-success') : 'bg-border'}`} />
                          )}
                          <div className="flex flex-col items-center" style={{ minWidth: '56px' }}>
                            <div className={`w-5 h-5 flex items-center justify-center text-[10px] font-bold border-2 ${
                              isRejected
                                ? 'border-error-border bg-error-subtle text-error'
                                : isCompleted
                                  ? 'border-success-border bg-success-subtle text-success'
                                  : isCurrent
                                    ? 'border-primary bg-primary-subtle text-primary'
                                    : 'border-border bg-muted text-muted-foreground'
                            }`}>
                              {isRejected ? (
                                <Icon name="X" size={10} />
                              ) : isCompleted ? (
                                <Icon name="Check" size={10} />
                              ) : (
                                i + 1
                              )}
                            </div>
                            <span className={`text-[9px] mt-0.5 leading-tight ${isCompleted || isCurrent ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                              {step}
                            </span>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}

                {/* Success banner (shown immediately after upload, before data refreshes) */}
                {justUploaded && (
                  <div className="rounded-lg px-3 py-2.5 mb-2.5 bg-success-subtle border border-success-border text-xs text-success flex items-center gap-2">
                    <Icon name="CheckCircle" size={14} className="text-success flex-shrink-0" />
                    <span className="font-semibold">{t('mySubmissions.cameraReady.uploadSuccess')}</span>
                    <span className="text-success">{t('mySubmissions.cameraReady.confirmationEmailSent')}</span>
                  </div>
                )}

                {/* Error banner */}
                {hasError && errorMsg && (
                  <div className="rounded-lg px-3 py-2.5 mb-2.5 bg-error-subtle border border-error-border text-xs text-error flex items-center gap-2">
                    <Icon name="AlertCircle" size={14} className="text-error flex-shrink-0" />
                    <span className="font-medium">{errorMsg}</span>
                    <button
                      onClick={() => { setErrorForId(null); setErrorMsg(''); }}
                      className="ml-auto text-error hover:text-error"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                )}

                {/* Row 3: Next Action / What to do */}
                {nextAction.type !== 'none' && !justUploaded && (
                  <div className={`rounded-lg px-3 py-2 mb-2.5 flex items-center gap-2 text-xs ${
                    nextAction.type === 'action'
                      ? 'bg-primary-subtle border border-primary/30 text-primary'
                      : nextAction.type === 'overdue'
                        ? 'bg-error-subtle border border-error-border text-error'
                        : nextAction.type === 'waiting'
                          ? 'bg-warning-subtle/60 border border-warning-border/60 text-warning'
                          : 'bg-success-subtle/60 border border-success-border/60 text-success'
                  }`}>
                    <Icon
                      name={
                        nextAction.type === 'action' ? 'ArrowRight'
                        : nextAction.type === 'overdue' ? 'AlertTriangle'
                        : nextAction.type === 'waiting' ? 'Clock'
                        : 'CheckCircle'
                      }
                      size={13}
                      className="flex-shrink-0"
                    />
                    <span className="font-medium flex-1">{nextAction.text}</span>

                    {/* Inline action buttons */}
                    {nextAction.action === 'continue' && (
                      <Button
                        size="xs"
                        onClick={() => navigate(`/author-submission-portal?conferenceId=${submission?.conferenceId}`)}
                        className="ml-auto"
                      >
                        {submission?.status === 'draft' ? t('mySubmissions.buttons.continue') : t('mySubmissions.buttons.revise')}
                      </Button>
                    )}
                    {nextAction.action === 'camera_ready' && (
                      <Button
                        size="xs"
                        onClick={() => handleCameraReadyClick(submission?.id)}
                        disabled={isUploading}
                        className="ml-auto bg-primary hover:bg-primary"
                      >
                        {isUploading ? (
                          <span className="flex items-center gap-1">
                            <Icon name="Loader" size={11} className="animate-spin" />
                            {t('mySubmissions.buttons.uploading')}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <Icon name="Upload" size={11} />
                            {t('mySubmissions.buttons.upload')}
                          </span>
                        )}
                      </Button>
                    )}
                  </div>
                )}

                {/* Accepted authors owe two documents: the paper itself and the
                    signed permission form. They're tracked separately because a
                    conference can be missing either one, and a single combined
                    indicator would hide which. Nothing here blocks — this is a
                    reminder, not a gate. */}
                {isAccepted && (
                  <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 mb-2.5 space-y-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('mySubmissions.finalDocuments', 'Final documents')}
                    </p>

                    {/* Camera-ready */}
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-1.5 text-xs min-w-0">
                        <Icon
                          name={hasCR || justUploaded ? 'CheckCircle2' : 'Circle'}
                          size={13}
                          className={hasCR || justUploaded ? 'text-success flex-shrink-0' : 'text-muted-foreground flex-shrink-0'}
                        />
                        <span className="truncate text-foreground">
                          {t('mySubmissions.cameraReadyLabel', 'Camera-ready paper')}
                        </span>
                        <span className="text-muted-foreground flex-shrink-0">
                          {t('mySubmissions.cameraReadyFormats', '(Word)')}
                        </span>
                      </span>
                      <Button
                        size="xs"
                        variant={hasCR || justUploaded ? 'ghost' : 'outline'}
                        onClick={() => handleCameraReadyClick(submission?.id)}
                        disabled={isUploading}
                        iconName="Upload"
                        iconPosition="left"
                        iconSize={11}
                      >
                        {hasCR || justUploaded
                          ? t('mySubmissions.cameraReady.replace', 'Replace file')
                          : t('mySubmissions.buttons.upload')}
                      </Button>
                    </div>

                    {/* Permission request form */}
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-1.5 text-xs min-w-0">
                        <Icon
                          name={submission?.permissionFormUrl ? 'CheckCircle2' : 'Circle'}
                          size={13}
                          className={submission?.permissionFormUrl ? 'text-success flex-shrink-0' : 'text-muted-foreground flex-shrink-0'}
                        />
                        <span className="truncate text-foreground">
                          {t('mySubmissions.permissionForm.label', 'Permission request form')}
                        </span>
                        <span className="text-muted-foreground flex-shrink-0">
                          {t('mySubmissions.permissionForm.pdfOnly', '(PDF)')}
                        </span>
                      </span>
                      <Button
                        size="xs"
                        variant={submission?.permissionFormUrl ? 'ghost' : 'outline'}
                        onClick={() => handlePermissionFormClick(submission?.id)}
                        disabled={isUploading}
                        iconName="Upload"
                        iconPosition="left"
                        iconSize={11}
                      >
                        {submission?.permissionFormUrl
                          ? t('mySubmissions.cameraReady.replace', 'Replace file')
                          : t('mySubmissions.buttons.upload')}
                      </Button>
                    </div>

                    {/* Attendee badge — always available once accepted, unlike
                        the two documents above which can be missing. */}
                    <div className="flex items-center justify-between gap-3 pt-1.5 border-t border-border/60">
                      <span className="flex items-center gap-1.5 text-xs min-w-0">
                        <Icon name="IdCard" size={13} className="text-muted-foreground flex-shrink-0" />
                        <span className="truncate text-foreground">
                          {t('mySubmissions.badge.label', 'Attendee badge')}
                        </span>
                      </span>
                      <BadgeDownloadButton conferenceId={submission?.conferenceId} size="xs" />
                    </div>
                  </div>
                )}

                {submission?.revisionSeverity && submission?.revisionNote && (
                  <div className="rounded-lg border border-accent-border bg-accent-subtle/60 px-3 py-2.5 mb-2.5">
                    <p className="text-xs font-semibold text-accent flex items-center gap-1.5">
                      <Icon name="PencilLine" size={13} />
                      {t(
                        submission.revisionSeverity === 'major'
                          ? 'mySubmissions.revision.majorTitle'
                          : 'mySubmissions.revision.minorTitle',
                        submission.revisionSeverity === 'major'
                          ? 'Major revisions requested'
                          : 'Minor revisions requested'
                      )}
                    </p>
                    <p className="text-xs text-foreground mt-1 leading-relaxed whitespace-pre-wrap">
                      {submission.revisionNote}
                    </p>
                  </div>
                )}

                {/* Row 4: Files + Reviews info row */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground">
                  {/* Original submission file */}
                  {submission?.pdfFileName && (
                    <span className="flex items-center gap-1">
                      <Icon name="FileText" size={11} className="text-primary" />
                      <span className="font-medium text-foreground">{submission?.pdfFileName}</span>
                      {submission?.pdfFileSize && (
                        <span>({formatFileSize(submission?.pdfFileSize)})</span>
                      )}
                    </span>
                  )}

                  {/* Camera-ready file */}
                  {isAccepted && (hasCR || justUploaded) && (
                    <span className="flex items-center gap-1">
                      <Icon name="FileCheck" size={11} className="text-success" />
                      <span className="font-medium text-success">
                        CR: {submission?.cameraReadyFileName || t('mySubmissions.cameraReady.uploaded')}
                      </span>
                      {submission?.cameraReadySubmittedAt && (
                        <span>· {t('mySubmissions.cameraReady.updatedAt', 'Updated')} {formatDateTime(submission?.cameraReadySubmittedAt)}</span>
                      )}
                      <button
                        onClick={() => handleCameraReadyClick(submission?.id)}
                        disabled={isUploading}
                        className="ml-1 inline-flex items-center gap-1 text-primary hover:text-primary font-medium disabled:opacity-50"
                      >
                        {isUploading ? (
                          <><Icon name="Loader" size={11} className="animate-spin" /> {t('mySubmissions.buttons.uploading')}</>
                        ) : (
                          <><Icon name="Upload" size={11} /> {t('mySubmissions.cameraReady.replace', 'Replace file')}</>
                        )}
                      </button>
                    </span>
                  )}

                  {/* Review scores */}
                  {submission?.reviewInfo?.completedReviews > 0 && (
                    <span className="flex items-center gap-1">
                      <Icon name="Star" size={11} className="text-warning" />
                      {t('mySubmissions.reviews.reviewCount', { count: submission?.reviewInfo?.completedReviews })}
                      {submission?.reviewInfo?.averageScore != null && (
                        <span> &middot; {t('mySubmissions.reviews.avgScore', { score: submission?.reviewInfo?.averageScore?.toFixed(1) })}</span>
                      )}
                    </span>
                  )}

                  {/* Review progress bar (for under_review) */}
                  {submission?.status === 'under_review' && submission?.reviewInfo?.totalReviews > 0 && (
                    <span className="flex items-center gap-1.5 ml-auto">
                      <div className="w-16 h-1.5 bg-muted overflow-hidden">
                        <div
                          className={`h-full ${
                            submission?.reviewInfo?.completedReviews === submission?.reviewInfo?.totalReviews
                              ? 'bg-success' : 'bg-warning'
                          }`}
                          style={{ width: `${(submission?.reviewInfo?.completedReviews / submission?.reviewInfo?.totalReviews) * 100}%` }}
                        />
                      </div>
                      <span>{submission?.reviewInfo?.completedReviews}/{submission?.reviewInfo?.totalReviews}</span>
                    </span>
                  )}

                  {/* Submitted date (push to right) */}
                  <span className="ml-auto flex items-center gap-1">
                    <Icon name="Calendar" size={11} />
                    {formatDate(submission?.submittedAt || submission?.createdAt)}
                  </span>
                </div>

                {/* Row 5: Secondary actions */}
                <div className="flex items-center justify-end gap-1.5 mt-2">
                  {submission?.status !== 'draft' && (
                    <Button variant="outline" size="xs" onClick={() => navigate(`/conference-landing-page?conferenceId=${submission?.conferenceId}`)}>
                      {t('mySubmissions.buttons.viewConference')}
                    </Button>
                  )}
                </div>
              </div>
    </div>
  );
};

export default SubmissionCard;
